import { useState, type ChangeEvent, type FormEvent } from 'react';
import { getProvider, type BlogPostDraft } from '@publishweaver/ai-providers';
import { useAdminSession } from '@/hooks/useAdminSession';
import { useAiSettings } from '@/hooks/useAiSettings';
import { extractTextFromHtml } from '@/lib/extractText';
import { adminApi } from '@/lib/adminApi';

interface Props {
  onGenerated: (draft: BlogPostDraft) => void;
  onCancel: () => void;
}

type InputMode = 'file' | 'url' | 'text';

export const GeneratePostPage = ({ onGenerated, onCancel }: Props) => {
  const { token } = useAdminSession();
  const { settings, toProviderConfig } = useAiSettings();
  const [inputMode, setInputMode] = useState<InputMode>('url');
  const [url, setUrl] = useState('');
  const [rawText, setRawText] = useState('');
  const [hint, setHint] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        // Basic HTML detection
        const text = result.trimStart().startsWith('<') ? extractTextFromHtml(result) : result;
        setRawText(text);
      }
    };
    reader.readAsText(file);
  };

  const fetchUrlContent = async (): Promise<string> => {
    if (!token) throw new Error('Not authenticated');
    const { text } = await adminApi.fetchUrl(token, url);
    return extractTextFromHtml(text);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setGenerating(true);
    try {
      let content = '';
      if (inputMode === 'url') {
        content = await fetchUrlContent();
      } else if (inputMode === 'file' || inputMode === 'text') {
        content = rawText.trim();
      }

      if (!content) {
        throw new Error('No content to generate from. Please provide a URL, file, or paste text.');
      }

      const provider = getProvider(settings.providerId);
      const draft = await provider.generateBlogPost({
        content,
        hint: hint.trim() || undefined,
        config: toProviderConfig(),
      });
      onGenerated(draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <span className="admin-topbar__brand">Studio — Generate Post</span>
        <button className="admin-btn admin-btn--ghost" onClick={onCancel}>Cancel</button>
      </header>

      <main className="admin-main">
        <div className="admin-section-header">
          <h2 className="admin-section-title">Generate from source</h2>
          <span className="admin-badge admin-badge--draft">{settings.providerId}</span>
        </div>

        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-field">
            <span className="admin-label">Source type</span>
            <div className="admin-toggle-row">
              {(['url', 'file', 'text'] as InputMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`admin-toggle-btn${inputMode === m ? ' admin-toggle-btn--active' : ''}`}
                  onClick={() => { setInputMode(m); setRawText(''); }}
                >
                  {m === 'url' ? 'URL' : m === 'file' ? 'File upload' : 'Paste text'}
                </button>
              ))}
            </div>
          </div>

          {inputMode === 'url' && (
            <div className="admin-field">
              <label htmlFor="gen-url" className="admin-label">Page URL</label>
              <input
                id="gen-url"
                type="url"
                className="admin-input admin-input--mono"
                value={url}
                onChange={(e) => { setUrl(e.target.value); }}
                placeholder="https://example.com/article"
                required
              />
            </div>
          )}

          {inputMode === 'file' && (
            <div className="admin-field">
              <label htmlFor="gen-file" className="admin-label">Document (.txt, .md, .html)</label>
              <input
                id="gen-file"
                type="file"
                className="admin-input"
                accept=".txt,.md,.html,.htm"
                onChange={handleFileChange}
                required={!rawText}
              />
              {rawText && (
                <span className="admin-settings__hint">
                  {rawText.length.toLocaleString()} characters loaded.
                </span>
              )}
            </div>
          )}

          {inputMode === 'text' && (
            <div className="admin-field admin-field--grow">
              <label htmlFor="gen-text" className="admin-label">Paste content</label>
              <textarea
                id="gen-text"
                className="admin-input admin-input--textarea"
                value={rawText}
                onChange={(e) => { setRawText(e.target.value); }}
                placeholder="Paste article text, notes, or any source material here..."
                required
              />
            </div>
          )}

          <div className="admin-field">
            <label htmlFor="gen-hint" className="admin-label">Hint (optional)</label>
            <input
              id="gen-hint"
              type="text"
              className="admin-input"
              value={hint}
              onChange={(e) => { setHint(e.target.value); }}
              placeholder="e.g. Focus on the developer experience angle"
            />
          </div>

          {error && <p className="admin-error">{error}</p>}

          {settings.providerId === 'stub' && (
            <p className="admin-error">
              Using demo provider — configure a real AI provider in Settings for actual generation.
            </p>
          )}

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn" disabled={generating}>
              {generating ? 'Generating…' : 'Generate post'}
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};
